import { useAtom } from "jotai";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { canv } from "@/canv/global";
import type { Group } from "@/group";
import { minimapUpdateAtom } from "@/interface/AppStore";
import {
    MINIMAP_HEIGHT,
    MINIMAP_WIDTH,
    TICK_INTERVAL,
} from "@/interface/BottomBar/BottomRightBar/MiniMap/MiniMapWindow/constants";
import type {
    MiniMapRect,
    MiniMapTransform,
} from "@/interface/BottomBar/BottomRightBar/MiniMap/MiniMapWindow/types";
import {
    computeMiniMapTransform,
    drawMiniMapItems,
    drawViewport,
    getCappedDPR,
    getVisibleMiniMapRects,
    serializeMiniMapToMainCanvCoordinates,
} from "@/interface/BottomBar/BottomRightBar/MiniMap/MiniMapWindow/utils";
import styles from "./MiniMapWindow.module.scss";

export const MiniMapWindow = () => {
    const [needUpdate, setNeedUpdate] = useAtom(minimapUpdateAtom);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const transformRef = useRef<MiniMapTransform | null>(null);
    const visibleRectsRef = useRef<MiniMapRect[] | null>(null);
    const offscreenRef = useRef<HTMLCanvasElement | null>(null);
    const itemsDirtyRef = useRef(true);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const drawItems = useCallback(
        (
            canvas: HTMLCanvasElement,
            items: Group[],
            transform: MiniMapTransform,
            updateCache: boolean,
        ) => {
            const offscreen = offscreenRef.current;
            if (!offscreen) return;

            if (!updateCache && !itemsDirtyRef.current) return;

            const dpr = getCappedDPR();

            offscreen.width = canvas.width * dpr;
            offscreen.height = canvas.height * dpr;

            const ctx = offscreen.getContext("2d");
            if (!ctx) return;

            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

            let visibleRects: MiniMapRect[];

            if (updateCache || visibleRectsRef.current === null) {
                visibleRects = getVisibleMiniMapRects(items, transform);
                visibleRectsRef.current = visibleRects;
            } else {
                visibleRects = visibleRectsRef.current;
            }

            ctx.clearRect(0, 0, offscreen.width / dpr, offscreen.height / dpr);

            drawMiniMapItems(ctx, visibleRects);

            itemsDirtyRef.current = false;
        },
        [],
    );

    const drawViewportOnly = useCallback(
        (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, transform: MiniMapTransform) => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const offscreen = offscreenRef.current;
            if (offscreen) {
                ctx.drawImage(offscreen, 0, 0, canvas.width, canvas.height);
            }
            if (visibleRectsRef.current && visibleRectsRef.current.length > 0) {
                drawViewport(ctx, transform);
            }
        },
        [],
    );
    const drawMiniMap = useCallback(
        (updateItems: boolean = false) => {
            const canvas = canvasRef.current;
            if (!canvas) return;

            const ctx = canvas.getContext("2d");
            if (!ctx) return;

            const controller = canv?.canvasController;
            const items = controller.items;

            const bound = controller?.getSelectedProps(items);
            if (!bound) return;

            const transform = computeMiniMapTransform(canvas, bound);
            transformRef.current = transform;

            drawItems(canvas, items, transform, updateItems);

            drawViewportOnly(ctx, canvas, transform);
        },
        [drawItems, drawViewportOnly],
    );

    const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        const transform = transformRef.current;

        if (
            !canvas ||
            !transform ||
            !visibleRectsRef.current ||
            visibleRectsRef.current?.length === 0
        )
            return;

        const rect = canvas.getBoundingClientRect();

        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        const canvPoint = serializeMiniMapToMainCanvCoordinates(clickX, clickY, transform);

        canv?.setViewportCenter?.(canvPoint.x, canvPoint.y);
    }, []);

    useLayoutEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = MINIMAP_WIDTH;
        canvas.height = MINIMAP_HEIGHT;
    }, []);

    useEffect(() => {
        offscreenRef.current = document.createElement("canvas");
        const tick = () => {
            drawMiniMap(true);
            timerRef.current = setTimeout(tick, TICK_INTERVAL);
        };
        tick();

        return () => {
            offscreenRef.current = null;
            visibleRectsRef.current = null;
            transformRef.current = null;
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [drawMiniMap]);

    useEffect(() => {
        if (!needUpdate) return;

        itemsDirtyRef.current = true;
        setNeedUpdate(false);
        drawMiniMap(true);
    }, [needUpdate, drawMiniMap, setNeedUpdate]);

    return (
        <div className={styles.miniMapWindow}>
            <canvas
                ref={canvasRef}
                onClick={handleCanvasClick}
                style={{ width: MINIMAP_WIDTH, height: MINIMAP_HEIGHT }}
            />
        </div>
    );
};
